import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { useToggleLuxPlusRestriction } from '~/hooks/apis/offers/useToggleLuxPlusRestriction';

import { scheduleForType } from '~/utils/scheduleForType';

import CampaignScheduleBrandGroupModal from './CampaignScheduleBrandGroupModal';
import CampaignScheduleBrandRow, { SHARED_WINDOW_TYPES } from './CampaignScheduleBrandRow';
import { buildCampaignScheduleBrandRows } from './campaignScheduleBrandRows';
import { toggleExpandedRow } from './campaignScheduleTabLayout';
import type { BrandSchedule } from './types';

interface Props {
  emptyMessage: string;
  offerId: string | undefined;
  schedules: Array<BrandSchedule>;
}

export default function CampaignScheduleBrandRowsTable({ emptyMessage, offerId, schedules }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Keyed lookup (not the row object itself) so the modal picks up fresh data after a save/delete
  // invalidates the offer query and brandRows is recomputed, instead of showing a stale snapshot.
  const [manageRowKey, setManageRowKey] = useState<string | undefined>();
  const brandRows = useMemo(() => buildCampaignScheduleBrandRows(schedules), [schedules]);
  const manageRow = brandRows.find((row) => row.key === manageRowKey);
  const toggleRestriction = useToggleLuxPlusRestriction(offerId);

  const sharedWindowByType = useMemo(
    () =>
      Object.fromEntries(
        SHARED_WINDOW_TYPES.map((type) => [
          type,
          { start: scheduleForType(schedules, type, 'start'), end: scheduleForType(schedules, type, 'end') },
        ]),
      ) as Record<BrandSchedule['type'], { start: string | undefined; end: string | undefined }>,
    [schedules],
  );

  if (brandRows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }} variant="body2">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Schedule</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Dates</TableCell>
            <TableCell>Regions</TableCell>
            <TableCell sx={{ width: 132 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {brandRows.map((row) => (
            <CampaignScheduleBrandRow
              key={row.key}
              isExpanded={expandedRows.has(row.key)}
              isTogglingRestriction={toggleRestriction.isPending}
              offerId={offerId}
              row={row}
              sharedWindowByType={sharedWindowByType}
              onManage={() => setManageRowKey(row.key)}
              onToggleExpand={() => setExpandedRows((current) => toggleExpandedRow(current, row.key))}
              onToggleRestriction={(restrict) =>
                toggleRestriction.mutate(
                  { schedules: row.schedules, restrict },
                  {
                    onError: () =>
                      enqueueSnackbar(`Failed to update restriction for ${row.brand}`, { variant: 'error' }),
                  },
                )
              }
            />
          ))}
        </TableBody>
      </Table>
      {offerId && (
        <CampaignScheduleBrandGroupModal
          offerId={offerId}
          open={!!manageRowKey}
          row={manageRow}
          onClose={() => setManageRowKey(undefined)}
        />
      )}
    </>
  );
}
